import type { ChartOfAccountsTemplate } from "@common/types/chartOfAccountsTemplate";
import { EntityBase, EntityProfileType } from "@common/types/entity";

export enum EntityApplicabilityRejection {
    NOT_A_REAL_BUSINESS = "notARealBusiness",
    LEGAL_TYPE_MISMATCH = "legalTypeMismatch",
    NOT_ON_GL_FIRST_CUSTOM_CHART = "notOnGlFirstCustomChart",
}

export interface IsEntityApplicableParams {
    entityLegalType: EntityProfileType | null;
    isBusiness: boolean;
    isMockEntity: boolean;
    hasCustomChartOfAccounts: boolean;
    isGlFirst: boolean;
    template: Pick<
        ChartOfAccountsTemplate,
        "entityLegalType" | "hasCustomChartOfAccounts"
    >;
}

export function getEntityApplicabilityRejection({
    entityLegalType,
    isBusiness,
    isMockEntity,
    hasCustomChartOfAccounts,
    isGlFirst,
    template,
}: IsEntityApplicableParams): EntityApplicabilityRejection | null {
    if (!isBusiness || isMockEntity) {
        return EntityApplicabilityRejection.NOT_A_REAL_BUSINESS;
    }
    if (
        entityLegalType === null ||
        entityLegalType !== template.entityLegalType
    ) {
        return EntityApplicabilityRejection.LEGAL_TYPE_MISMATCH;
    }
    if (
        template.hasCustomChartOfAccounts &&
        !(hasCustomChartOfAccounts && isGlFirst)
    ) {
        return EntityApplicabilityRejection.NOT_ON_GL_FIRST_CUSTOM_CHART;
    }
    return null;
}

export function isEntityApplicable(params: IsEntityApplicableParams): boolean {
    return getEntityApplicabilityRejection(params) === null;
}

export interface IsTemplateAllowedForNewEntityParams {
    template: Pick<ChartOfAccountsTemplate, "hasCustomChartOfAccounts">;
    isGlFirstEnabled: boolean;
}

export function isTemplateAllowedForNewEntity({
    template,
    isGlFirstEnabled,
}: IsTemplateAllowedForNewEntityParams): boolean {
    return !template.hasCustomChartOfAccounts || isGlFirstEnabled;
}

export interface IsEntityAllowedAsCopySourceParams {
    sourceEntity: Pick<EntityBase, "isPersonal" | "isMockEntity"> & {
        hasCustomChartOfAccounts: boolean;
    };
    targetWorkspace?: { isGlFirstEnabled: boolean };
}

export function isEntityAllowedAsCopySource({
    sourceEntity,
    targetWorkspace,
}: IsEntityAllowedAsCopySourceParams): boolean {
    if (sourceEntity.isPersonal || sourceEntity.isMockEntity) {
        return false;
    }
    if (!sourceEntity.hasCustomChartOfAccounts) {
        return true;
    }
    return targetWorkspace?.isGlFirstEnabled ?? true;
}
